import { buildDateRangeFilterValue } from "@/components/non_registrant/dashboard-table-date-filter";
import type { ManualCheckPaymentListItem } from "@/hera-api/manual-check-payments-api";
import { getPacificTodayYmd, parseDateAsLocal } from "@/lib/utils";
import { format, subDays } from "date-fns";

export const OVERDUE_INVOICE_DAYS = 30;
export const OVERDUE_INVOICE_STATUS = "Payment Pending";
export const OVERDUE_CHECK_STATUS = "Awaiting Check";

export function overdueInvoiceCutoffYmd(
  asOfYmd = getPacificTodayYmd(),
): string {
  const asOf = parseDateAsLocal(asOfYmd);
  if (!asOf) {
    return asOfYmd;
  }
  return format(subDays(asOf, OVERDUE_INVOICE_DAYS + 1), "yyyy-MM-dd");
}

export function overdueInvoiceColumnFilters(
  asOfYmd = getPacificTodayYmd(),
): Record<string, string[]> {
  return overdueReceivableColumnFilters(OVERDUE_INVOICE_STATUS, asOfYmd);
}

export function overdueCheckColumnFilters(
  asOfYmd = getPacificTodayYmd(),
): Record<string, string[]> {
  return overdueReceivableColumnFilters(OVERDUE_CHECK_STATUS, asOfYmd);
}

function overdueReceivableColumnFilters(
  status: string,
  asOfYmd: string,
): Record<string, string[]> {
  return {
    status: [status],
    created_at:
      buildDateRangeFilterValue(null, overdueInvoiceCutoffYmd(asOfYmd)) ?? [],
  };
}

export function pacificCalendarDateYmd(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  return year && month && day ? `${year}-${month}-${day}` : null;
}

export function isInvoiceOverdue(
  invoice: ManualCheckPaymentListItem,
  asOfYmd = getPacificTodayYmd(),
): boolean {
  return isReceivableOverdue(invoice, OVERDUE_INVOICE_STATUS, asOfYmd);
}

export function isCheckOverdue(
  check: ManualCheckPaymentListItem,
  asOfYmd = getPacificTodayYmd(),
): boolean {
  return isReceivableOverdue(check, OVERDUE_CHECK_STATUS, asOfYmd);
}

function isReceivableOverdue(
  item: ManualCheckPaymentListItem,
  status: string,
  asOfYmd: string,
): boolean {
  if (item.status !== status) {
    return false;
  }
  const issuedYmd = pacificCalendarDateYmd(item.created_at);
  return issuedYmd !== null && issuedYmd <= overdueInvoiceCutoffYmd(asOfYmd);
}

export function invoiceTotalAmount(
  invoice: ManualCheckPaymentListItem,
): number {
  return (
    Number.parseFloat(invoice.total_registration_fee || "0") +
    Number.parseFloat(invoice.total_late_fee || "0") +
    Number.parseFloat(invoice.total_processing_fee || "0") +
    Number.parseFloat(invoice.total_inspection_fee || "0")
  );
}
